import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { reports, taxonomies } from '../data/mockData';

const Portal = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('portalFilters');
    return saved ? JSON.parse(saved) : { region: [], sector: [], year: [], country: [] };
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('reportViewMode') || 'grid';
  });

  const [expandedGroups, setExpandedGroups] = useState({
    Regions: false,
    Countries: false,
    Sectors: false,
    Years: false
  });

  // Sync URL Params to State (Fix for Search Selection Failure)
  useEffect(() => {
    const urlQuery = searchParams.get('search');
    const urlFilter = searchParams.get('filter');
    const urlType = searchParams.get('type');

    if (urlQuery && !urlType) {
      setSearchQuery(urlQuery);
    } else if (urlFilter && urlType) {
      handleFilterChange(urlType, urlFilter);
      // Open the corresponding accordion for visual feedback
      const groupName = urlType.charAt(0).toUpperCase() + urlType.slice(1) + 's';
      toggleGroup(groupName === 'Countrys' ? 'Countries' : groupName);
      // Clean up URL after applying
      searchParams.delete('filter');
      searchParams.delete('type');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem('portalFilters', JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    localStorage.setItem('reportViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cleanCountryName = (loc) => {
    if (loc.includes('(')) return loc.split('(')[0].trim();
    const indianStates = ['Uttar Pradesh', 'Bihar', 'Rajasthan', 'Madhya Pradesh', 'West Bengal', 'Jharkhand', 'Odisha', 'Chhattisgarh', 'Punjab', 'Haryana', 'Maharashtra', 'Gujarat', 'Karnataka', 'Tamil Nadu', 'Andhra Pradesh', 'Telangana', 'Kerala', 'Assam', 'Manipur', 'Meghalaya', 'Tripura', 'Nagaland', 'Sikkim', 'Arunachal Pradesh'];
    if (indianStates.includes(loc)) return 'India';
    if (loc === 'West Bank' || loc.includes('Gaza')) return 'Palestine';
    return loc;
  };

  const availableCountries = useMemo(() => {
    const all = reports.flatMap(r => r.countries.map(cleanCountryName));
    return [...new Set(all)].sort();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      const query = searchQuery.trim().toLowerCase();
      if (query.length > 1) {
        const pool = [
          ...availableCountries.map(c => ({ label: c, type: 'country', icon: 'FiFlag' })),
          ...taxonomies.regions.map(r => ({ label: r, type: 'region', icon: 'FiGlobe' })),
          ...taxonomies.sectors.map(s => ({ label: s, type: 'sector', icon: 'FiBox' })),
          ...taxonomies.years.map(y => ({ label: y, type: 'year', icon: 'FiCalendar' }))
        ];

        const filtered = pool.filter(item => 
          item.label.toLowerCase().includes(query) && !filters[item.type].includes(item.label)
        ).slice(0, 8);

        setSuggestions(filtered);
        setShowSuggestions(filtered.length > 0);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 200);
    return () => clearTimeout(handler);
  }, [searchQuery, availableCountries, filters]);

  const handleFilterChange = (category, value) => {
    setFilters(prev => {
      const catKey = category.toLowerCase();
      const current = prev[catKey] || [];
      if (current.includes(value)) {
        return { ...prev, [catKey]: current.filter(item => item !== value) };
      } else {
        return { ...prev, [catKey]: [...current, value] };
      }
    });
  };

  const toggleGroup = (title) => {
    setExpandedGroups(prev => {
      const isCurrentlyOpen = prev[title];
      const newState = { Regions: false, Countries: false, Sectors: false, Years: false };
      if (!isCurrentlyOpen) {
        newState[title] = true;
      }
      return newState;
    });
  };

  const setAllGroups = (state) => {
    setExpandedGroups({ Regions: state, Countries: state, Sectors: state, Years: state });
  };

  const clearFilters = () => {
    setFilters({ region: [], sector: [], year: [], country: [] });
    setSearchQuery('');
    setSearchParams({});
  };

  const globalMetrics = useMemo(() => {
    const totalLives = reports.reduce((acc, curr) => acc + curr.metrics.beneficiaries, 0);
    const totalProjects = reports.length;
    const uniqueRegions = new Set(reports.map(r => r.region)).size;
    return [
      { label: 'Total Lives Impacted', value: totalLives.toLocaleString() + '+', icon: 'FiUsers', description: 'Individuals reached globally' },
      { label: 'Projects Delivered', value: totalProjects.toLocaleString(), icon: 'FiCheckCircle', description: 'Verified field initiatives' },
      { label: 'Countries Supported', value: (uniqueRegions * 4).toString() + '+', icon: 'FiGlobe', description: 'Active across 4 major regions' }
    ];
  }, []);

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchRegion = filters.region.length === 0 || filters.region.includes(report.region);
      const matchSector = filters.sector.length === 0 || filters.sector.includes(report.sector);
      const matchYear = filters.year.length === 0 || filters.year.includes(report.year);
      const reportCleanCountries = report.countries.map(cleanCountryName);
      const matchCountry = filters.country.length === 0 || reportCleanCountries.some(c => filters.country.includes(c));
      const matchSearch = report.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          report.summary.toLowerCase().includes(searchQuery.toLowerCase());

      return matchRegion && matchSector && matchYear && matchCountry && matchSearch;
    });
  }, [filters, searchQuery]);

  const activeFilterCount = filters.region.length + filters.sector.length + filters.year.length + filters.country.length;

  return (
    <div className="bg-[#F9FAFB] min-h-screen">
      <section className="bg-[#0B0D0F] pt-40 pb-28 md:pt-48 md:pb-44 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&w=2000" alt="Background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0D0F] via-transparent to-[#0B0D0F]"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: "easeOut" }}>
            <span className="inline-block bg-[#E52321]/10 text-[#E52321] px-4 py-1.5 rounded-full text-[10px] font-black tracking-[0.25em] uppercase mb-6 border border-[#E52321]/20">
              Verified Transparency
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-6 sm:mb-8 tracking-tighter leading-[1.1] sm:leading-[0.9] uppercase">
              Global Projects <br className="hidden sm:block" />
              <span className="text-[#E52321]">Impact Reports</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed px-4">
              A comprehensive database of our global initiatives, ensuring every contribution is accounted for through verified field documentation.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 md:-mt-20 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {globalMetrics.map((metric, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + (idx * 0.1), duration: 0.6 }} whileHover={{ y: -8 }} className="bg-white rounded-[2rem] p-6 sm:p-8 shadow-[0_20px_50px_rgba(0,0,0,0.06)] border border-gray-100 flex items-center group transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#0B0D0F] rounded-2xl flex items-center justify-center mr-5 sm:mr-6 group-hover:bg-[#E52321] transition-colors duration-500 shadow-lg shadow-black/10 group-hover:shadow-red-500/20 flex-shrink-0">
                <SafeIcon icon={metric.icon} className="text-white text-xl sm:text-2xl" />
              </div>
              <div className="text-left overflow-hidden">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 truncate">{metric.label}</p>
                <p className="text-2xl sm:text-3xl font-black text-black tracking-tighter leading-none mb-1">{metric.value}</p>
                <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium truncate">{metric.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row items-center gap-4 mb-12 sm:mb-16">
          <div ref={searchContainerRef} className="flex-grow w-full relative group">
            <SafeIcon icon="FiSearch" className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-lg group-focus-within:text-[#E52321] transition-colors" />
            <input 
              type="text" 
              placeholder="Search by Country, Region, Sector, or Year" 
              value={searchQuery} 
              onChange={(e) => setSearchQuery(e.target.value)} 
              onFocus={() => searchQuery.length > 1 && setShowSuggestions(suggestions.length > 0)}
              className="w-full bg-white border border-gray-100 rounded-2xl py-4 sm:py-5 pl-14 pr-6 text-sm font-medium shadow-xl shadow-black/5 focus:ring-2 focus:ring-[#E52321]/10 transition-all outline-none" 
            />
            <AnimatePresence>
              {showSuggestions && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute top-full left-0 right-0 mt-3 bg-white border border-gray-100 rounded-[1.5rem] shadow-[0_25px_60px_rgba(0,0,0,0.12)] z-50 overflow-hidden py-3" >
                  <div className="px-6 py-2 mb-1 border-b border-gray-50 flex items-center justify-between">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Matched Filters</span>
                    <SafeIcon icon="FiFilter" className="text-gray-300 text-xs" />
                  </div>
                  {suggestions.map((suggestion, idx) => (
                    <button key={idx} onClick={() => { handleFilterChange(suggestion.type, suggestion.label); setSearchQuery(''); setShowSuggestions(false); }} className="w-full px-6 py-4 text-left text-sm font-bold text-gray-700 hover:bg-gray-50 hover:text-[#E52321] flex items-center transition-all duration-200 group" >
                      <div className="w-8 h-8 rounded-lg bg-gray-50 group-hover:bg-[#E52321]/10 flex items-center justify-center mr-4 transition-colors">
                        <SafeIcon icon={suggestion.icon} className="text-gray-400 group-hover:text-[#E52321] text-xs" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{suggestion.type}</span>
                        <span className="flex-grow">{suggestion.label}</span>
                      </div>
                      <SafeIcon icon="FiPlus" className="ml-auto text-gray-200 group-hover:text-[#E52321] opacity-0 group-hover:opacity-100 transition-all" />
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="flex bg-white p-1.5 rounded-2xl shadow-sm border border-gray-100 w-full md:w-auto">
            <button onClick={() => setViewMode('grid')} className={`flex-1 md:flex-none flex items-center justify-center space-x-2 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${viewMode === 'grid' ? 'bg-[#0B0D0F] text-white shadow-lg' : 'text-gray-400 hover:text-black hover:bg-gray-50'}`} >
              <SafeIcon icon="FiGrid" className="text-base" />
              <span>Grid</span>
            </button>
            <button onClick={() => setViewMode('list')} className={`flex-1 md:flex-none flex items-center justify-center space-x-2 px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 ${viewMode === 'list' ? 'bg-[#0B0D0F] text-white shadow-lg' : 'text-gray-400 hover:text-black hover:bg-gray-50'}`} >
              <SafeIcon icon="FiList" className="text-base" />
              <span>List</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          <aside className="w-full lg:w-64 flex-shrink-0">
            <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
              <h3 className="text-[11px] font-black text-black uppercase tracking-widest">Filters</h3>
              <div className="flex gap-4">
                <button onClick={() => setAllGroups(true)} className="text-[9px] font-bold uppercase text-gray-400 hover:text-[#E52321] transition-colors" > Expand </button>
                <button onClick={() => setAllGroups(false)} className="text-[9px] font-bold uppercase text-gray-400 hover:text-[#E52321] transition-colors" > Collapse </button>
              </div>
            </div>
            <div className="space-y-6">
              <FilterGroup title="Regions" options={taxonomies.regions} selected={filters.region} isOpen={expandedGroups.Regions} onToggle={() => toggleGroup('Regions')} onOptionToggle={(val) => handleFilterChange('region', val)} />
              <FilterGroup title="Countries" options={availableCountries} selected={filters.country} isOpen={expandedGroups.Countries} onToggle={() => toggleGroup('Countries')} onOptionToggle={(val) => handleFilterChange('country', val)} />
              <FilterGroup title="Sectors" options={taxonomies.sectors} selected={filters.sector} isOpen={expandedGroups.Sectors} onToggle={() => toggleGroup('Sectors')} onOptionToggle={(val) => handleFilterChange('sector', val)} />
              <FilterGroup title="Years" options={taxonomies.years} selected={filters.year} isOpen={expandedGroups.Years} onToggle={() => toggleGroup('Years')} onOptionToggle={(val) => handleFilterChange('year', val)} />
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="w-full mt-10 py-4 border-2 border-dashed border-gray-200 rounded-2xl text-[10px] font-black text-gray-400 hover:text-[#E52321] hover:border-[#E52321]/30 transition-all duration-300 uppercase tracking-widest" >
                Clear all filters
              </button>
            )}
          </aside>

          <div className="flex-grow">
            <motion.div layout className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8" : "flex flex-col space-y-4"} >
              <AnimatePresence mode="popLayout">
                {filteredReports.map((report) => (
                  <motion.div layout key={report.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }} >
                    {viewMode === 'grid' ? <ReportCard report={report} /> : <ReportListRow report={report} />}
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {filteredReports.length === 0 && (
              <div className="text-center py-32 bg-white rounded-[3rem] border border-gray-100 shadow-sm">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon="FiInbox" className="text-3xl text-gray-300" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Reports Found</h3>
                <p className="text-gray-500 max-w-xs mx-auto">Try adjusting your filters or search terms to find what you're looking for.</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const FilterGroup = ({ title, options, selected, isOpen, onToggle, onOptionToggle }) => (
  <div className="border-b border-gray-50 last:border-0 pb-4">
    <button onClick={onToggle} className="w-full flex items-center justify-between py-2 group cursor-pointer" >
      <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em] group-hover:text-black transition-all duration-300"> {title} </h4>
      <motion.div animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="text-gray-300 group-hover:text-[#E52321] transition-colors" >
        <SafeIcon icon="FiChevronRight" className="text-sm" />
      </motion.div>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} className="overflow-hidden" >
          <div className="space-y-1.5 pt-4 pb-2 text-left max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-200">
            {options.map((option) => (
              <button key={option} onClick={() => onOptionToggle(option)} className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs font-bold transition-all duration-200 border ${selected.includes(option) ? 'bg-[#E52321] text-white border-[#E52321] shadow-lg shadow-red-500/20' : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-[#E52321] hover:border-[#E52321]/30 border-gray-100 shadow-sm'}`} >
                <span className="text-left flex-grow truncate">{option}</span>
                {selected.includes(option) && <SafeIcon icon="FiCheck" className="ml-2 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const ReportCard = ({ report }) => (
  <div className="group bg-white rounded-[2.5rem] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 flex flex-col h-full text-left" >
    <div className="relative h-60 sm:h-64 overflow-hidden">
      <img src={report.image} alt={report.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
      <div className="absolute top-6 left-6 flex items-center space-x-2">
        <span className="bg-white/95 backdrop-blur-sm text-black text-[10px] font-black px-3 py-1.5 rounded-lg uppercase tracking-widest shadow-sm"> {report.year} </span>
      </div>
    </div>
    <div className="p-8 sm:p-10 flex flex-col flex-grow">
      <div className="flex items-center space-x-2 text-[#E52321] text-[10px] font-black uppercase tracking-[0.2em] mb-4">
        <SafeIcon icon="FiMapPin" />
        <span>{report.region}</span>
        <span className="text-gray-200 mx-2">•</span>
        <span>{report.sector}</span>
      </div>
      <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-4 leading-tight group-hover:text-[#E52321] transition-colors line-clamp-2"> {report.title} </h3>
      <p className="text-gray-500 text-sm leading-relaxed mb-8 line-clamp-2"> {report.summary} </p>
      <div className="mt-auto pt-6 border-t border-gray-50 flex justify-between items-center">
        <div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Impact</p>
          <p className="text-lg font-black text-gray-900">{report.metrics.beneficiaries.toLocaleString()}</p>
        </div>
        <Link to={`/report/${report.id}`} className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#E52321] group-hover:text-white transition-all transform group-hover:translate-x-1 duration-300" >
          <SafeIcon icon="FiArrowRight" className="text-xl" />
        </Link>
      </div>
    </div>
  </div>
);

const ReportListRow = ({ report }) => (
  <div className="group bg-white rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-6 border border-gray-100 hover:shadow-xl hover:shadow-black/5 hover:border-[#E52321]/20 transition-all duration-300 text-left" >
    <div className="w-full sm:w-32 h-32 sm:h-24 rounded-xl overflow-hidden flex-shrink-0 relative">
      <img src={report.image} alt={report.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
    </div>
    <div className="flex-grow">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <span className="text-[9px] font-black bg-gray-50 text-gray-400 px-2.5 py-1 rounded-md uppercase tracking-wider"> {report.year} </span>
        <span className="text-[9px] font-black text-[#E52321] uppercase tracking-[0.15em]"> {report.region} • {report.sector} </span>
      </div>
      <h3 className="text-lg font-black text-gray-900 mb-1 group-hover:text-[#E52321] transition-colors truncate max-w-md"> {report.title} </h3>
      <p className="text-gray-400 text-xs line-clamp-1"> {report.summary} </p>
    </div>
    <div className="flex items-center space-x-8 px-6 border-l border-gray-50 hidden md:flex">
      <div className="text-center">
        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Impact</p>
        <p className="text-base font-black text-gray-900">{report.metrics.beneficiaries.toLocaleString()}</p>
      </div>
    </div>
    <Link to={`/report/${report.id}`} className="w-full sm:w-auto bg-[#0B0D0F] hover:bg-[#E52321] text-white px-8 py-3.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center group/btn flex-shrink-0" >
      Report <SafeIcon icon="FiArrowRight" className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
    </Link>
  </div>
);

export default Portal;